import React, { useEffect } from 'react';

import { SafelyExitCourseOverlay } from '../../../ComponentLibrary/organisms/SafelyExitCourseOverlay/SafelyExitCourseOverlay';
import { getStudentViewModeFromString, StudentViewMode } from '../../../config/constants';
import { congratulationsMessages } from '../../../translates/congratulationsOverlayMessages';
import { useExitCourse } from '../../hooks/useExitCourse';
import { useIntlReexport } from '../../hooks/useIntlReexport';
import { useIsMobile } from '../../hooks/useIsMobile';
import { usePrevious } from '../../hooks/usePrevious';
import { useSwitch } from '../../hooks/useSwitch';
import { useCanDisplayCongratulationsSelector } from '../../store/selectors/useCanDisplayCongratulationsSelector';
import { useStudentSelector } from '../../store/selectors/useStudentSelector';
import { Button, ButtonVariant, CongratulationsOverlay, StudentButton } from '@ael/shared-components-library';

type CompletionOverlayProps = {
  projectName: string;
  isInPreview: boolean;
  viewMode: StudentViewMode;
  hideStudentHeader: boolean;
};

export const CompletionOverlay = (props: CompletionOverlayProps) => {
  const { formatMessage } = useIntlReexport();

  const showScormExitInstructions = useSwitch(false);

  const viewMode = getStudentViewModeFromString(props.viewMode);
  const exitCourse = useExitCourse(viewMode, showScormExitInstructions.turnOn, props.hideStudentHeader);

  const canDisplayCongratulationsOverlay = useCanDisplayCongratulationsSelector();
  const isCourseCompleted = useStudentSelector((state) => state.tracking.completed);
  const wasPreviouslyCompleted = usePrevious(isCourseCompleted);

  const dismissedSwitch = useSwitch(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!wasPreviouslyCompleted && isCourseCompleted) {
      dismissedSwitch.turnOff();
    }
  }, [
    dismissedSwitch,
    isCourseCompleted,
    canDisplayCongratulationsOverlay,
    showScormExitInstructions,
    wasPreviouslyCompleted,
  ]);

  if (dismissedSwitch.isOn || !canDisplayCongratulationsOverlay || !isCourseCompleted) {
    return null;
  } else if (showScormExitInstructions.isOn) {
    return <SafelyExitCourseOverlay />;
  } else {
    return (
      <CongratulationsOverlay
        isMobile={isMobile}
        title={formatMessage(congratulationsMessages.title)}
        subtitle={formatMessage(congratulationsMessages.subtitle, { coursename: props.projectName })}
        button={
          exitCourse && !props.isInPreview ? (
            <StudentButton onClick={exitCourse}>{formatMessage(congratulationsMessages.buttonExit)}</StudentButton>
          ) : (
            <StudentButton onClick={dismissedSwitch.turnOn}>
              {formatMessage(congratulationsMessages.buttonExplore)}
            </StudentButton>
          )
        }
        secondaryButton={
          exitCourse &&
          !props.isInPreview && (
            <Button variant={ButtonVariant.TEXT} onClick={dismissedSwitch.turnOn}>
              {formatMessage(congratulationsMessages.buttonExplore)}
            </Button>
          )
        }
      />
    );
  }
};
